import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ACCEPTED_IMAGE_TYPES,
  BrandingImage,
  ImageRejected,
  PreparedImage,
  prepareImage
} from '@/domain/branding'

interface ImageFieldProps {
  label: string
  hint: string
  image: BrandingImage
  // The url currently stored, shown until a new file is picked.
  storedUrl: string
  // A file picked but not saved yet. Null means the stored image is what the portal shows.
  pending: PreparedImage | null
  disabled: boolean
  onPick: (prepared: PreparedImage | null) => void
}

// An image attribute. Picking a file resizes and re-encodes it in the browser and previews it
// locally; nothing is uploaded until the page is saved, so the image obeys the same Save button
// and unsaved-changes guard as every other attribute here.
export function ImageField({
  label,
  hint,
  image,
  storedUrl,
  pending,
  disabled,
  onPick
}: ImageFieldProps) {
  const { t } = useTranslation()
  const inputId = useId()
  const [rejected, setRejected] = useState<ImageRejected['reason'] | null>(null)
  const [preparing, setPreparing] = useState(false)
  const [dragging, setDragging] = useState(false)

  const shownUrl = pending?.previewUrl || storedUrl

  async function pick(file: File | undefined) {
    if (!file) return
    setRejected(null)
    try {
      setPreparing(true)
      onPick(await prepareImage(file, image))
    } catch (error) {
      // Anything that is not a rejection is a browser fault rather than a bad file, and the
      // merchant can act on neither, so both read as "this file cannot be used".
      setRejected(error instanceof ImageRejected ? error.reason : 'unreadable')
    } finally {
      setPreparing(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-3">
        <span className="text-sm text-gray-500">{label}</span>
        {pending && !disabled && (
          <button
            type="button"
            className="text-xs text-gray-400 underline"
            onClick={() => {
              setRejected(null)
              onPick(null)
            }}
          >
            {t('custom_theme.image_undo')}
          </button>
        )}
      </div>

      {/* The row is a drop target as well as a picker: a logo usually sits in a folder next to
          the browser, and dragging it over beats hunting for it in a file dialog. */}
      <div
        className="flex items-center gap-3"
        onDragOver={(e) => {
          if (disabled || preparing) return
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={(e) => {
          // Crossing into the tile or the button fires a leave too; only leaving the row counts.
          if (e.currentTarget.contains(e.relatedTarget as Node)) return
          setDragging(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          if (disabled || preparing) return
          pick(e.dataTransfer.files?.[0])
        }}
      >
        {/* The tile is a label for the input too, so clicking the image itself opens the picker. */}
        <label
          htmlFor={inputId}
          className={
            'flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-white transition-colors ' +
            (dragging
              ? 'border-dashed border-primary-main'
              : 'border-gray-300 ' +
                (disabled || preparing
                  ? ''
                  : 'cursor-pointer hover:border-gray-400'))
          }
        >
          {shownUrl ? (
            <img
              className="max-h-full max-w-full object-contain"
              src={shownUrl}
              alt={label}
            />
          ) : (
            <span className="px-1 text-center text-[10px] leading-tight text-gray-400">
              {t('custom_theme.image_none')}
            </span>
          )}
        </label>

        <div className="flex flex-col gap-1">
          {/* The input itself is hidden because a browser file input cannot be styled, but it
              stays in the tree so the label activates it and the keyboard reaches it. */}
          <input
            id={inputId}
            type="file"
            className="sr-only"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            disabled={disabled || preparing}
            onChange={(e) => {
              pick(e.target.files?.[0])
              // Cleared so picking the same file again after an undo still fires a change.
              e.target.value = ''
            }}
          />
          {/* Border and padding match the segmented buttons rather than the text inputs: this
              is something to press, and gray-300 is the input border on this page. */}
          <label
            htmlFor={inputId}
            className={
              'w-fit rounded-md border border-gray-400 px-3 py-1 text-sm transition-all ' +
              (disabled || preparing
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'cursor-pointer bg-gray-50 hover:bg-gray-300')
            }
          >
            {preparing
              ? t('custom_theme.image_preparing')
              : shownUrl
                ? t('custom_theme.image_replace')
                : t('custom_theme.image_choose')}
          </label>
        </div>
      </div>

      {rejected ? (
        <span className="text-xs text-rose-600">
          {t(`custom_theme.image_rejected_${rejected}`)}
        </span>
      ) : (
        <span className="text-xs text-gray-400">{hint}</span>
      )}
    </div>
  )
}

export default ImageField
